// PLACEHOLDER: Shared click-to-edit cell — replace with full implementation from shared-components agent
import { useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { IEditableCellProps } from '@/@types/insurance';

export function EditableCell({ value, onSave, className, placeholder = '—', readOnly = false }: IEditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleClick() {
    if (readOnly) return;
    setDraft(value);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleBlur() {
    setEditing(false);
    if (draft !== value) {
      onSave(draft);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setDraft(value);
      setEditing(false);
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full rounded border border-ring bg-background px-1.5 py-0.5 text-sm outline-none focus:ring-1 focus:ring-ring',
          className
        )}
      />
    );
  }

  return (
    <span
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? undefined : 0}
      onClick={readOnly ? undefined : handleClick}
      onKeyDown={readOnly ? undefined : (e => e.key === 'Enter' && handleClick())}
      className={cn(
        'block w-full rounded px-1.5 py-0.5 text-sm',
        !readOnly && 'cursor-pointer hover:bg-muted/60 focus:outline-none focus:ring-1 focus:ring-ring',
        !value && 'text-muted-foreground',
        className
      )}
    >
      {value || placeholder}
    </span>
  );
}
